import { DocumentNode } from 'graphql';
import { groupBy } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo, useState } from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { HiddenSectionIndicators } from 'src/app/graph/shared/diffEditor/HiddenSectionIndicators';
import { useHideUnchangedLines } from 'src/app/graph/shared/diffEditor/useHideUnchangedLines';
import { WrapLinesButton } from 'src/app/graph/variantSettingsPage/variantSettingsCards/contractConfigWizard/contractConfigReviewModal/SchemaDiffViewer';
import { Loading } from 'src/components/common/loading/Loading';
import {
  StudioDiffEditorContainer,
  useStudioDiffEditor,
} from 'src/hooks/useStudioDiffEditor';
import { getLastFromMinLengthArray } from 'src/lib/getLastFromMinLengthArray';
import type { countAddedAndDeletedTypes } from 'src/lib/graphql/graphqlHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { SUPERGRAPH_SCHEMA_ID } from '../../constants';
import { Violation } from '../lintCheckList/ruleViolationList/RuleViolationList';
import { TypeAndFieldDiffCount } from '../typeAndFieldDiffCount/TypeAndFieldDiffCount';

import {
  EditorViolation,
  EditorViolationLinterRuleLevelConfigurationFragment,
  OverrideViolationButtonIgnoredRuleFragment,
  OverrideViolationButtonLintDiagnosticFragment,
} from './editorViolation/EditorViolation';
import { LintDiffNavigatorButtons } from './lintDiffNavigatorButtons/LintDiffNavigatorButtons';

export {
  OverrideViolationButtonIgnoredRuleFragment,
  EditorViolationLinterRuleLevelConfigurationFragment,
  OverrideViolationButtonLintDiagnosticFragment,
};

export const LintCheckCodeView = ({
  violationsBySubgraph,
  selectedSchema,
  baseSdl,
  proposedSdl,
  documentLoading,
  rules,
  ignoredLinterViolations,
  typeAndFieldCount,
  modifiedTypeDefs,
  originalTypeDefs,
}: {
  violationsBySubgraph: Record<string, Array<Violation>>;
  selectedSchema: string;
  baseSdl: string;
  proposedSdl: string;
  documentLoading: boolean;
  rules: GraphQLTypes.EditorViolationLinterRuleLevelConfigurationFragment[];
  ignoredLinterViolations: GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[];
  typeAndFieldCount: ReturnType<typeof countAddedAndDeletedTypes> | undefined;
  modifiedTypeDefs: DocumentNode;
  originalTypeDefs: DocumentNode;
}) => {
  const [shouldWrapLines, setShouldWrapLines] = useState(false);
  const { isProposalRoute } = useInternalGraphLinking();

  const sharedMonacoOptions = useMemo(
    ():
      | monacoEditor.editor.IStandaloneEditorConstructionOptions
      | monacoEditor.editor.IDiffEditorConstructionOptions => ({
      readOnly: true,
      wordWrap: shouldWrapLines ? 'on' : 'off',
      renderSideBySide: false,
      enableSplitViewResizing: false,
      fontSize: 12,
      scrollbar: {
        useShadows: false,
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8,
      },
      renderOverviewRuler: false,
    }),
    [shouldWrapLines],
  );

  // The diffEditor won't update files if schema is a falsy value so pass a space in to force reset
  const { diffEditor } = useStudioDiffEditor({
    monacoOptions: sharedMonacoOptions,
    language: 'graphql',
    // on proposal checks, we run the checks on a rebased sdl. This means the
    // diff view, which compares strings, will compare a normalized
    // sdl to a non normalized one. For now, we don't show diff highlighting
    // on proposal lint diff views
    originalFile: isProposalRoute ? proposedSdl : baseSdl,
    modifiedFile: proposedSdl,
  });

  useEffect(() => {
    diffEditor.getOriginalEditor().updateOptions({
      scrollbar: {
        vertical: 'hidden',
        horizontal: 'hidden',
        useShadows: false,
        verticalScrollbarSize: 0,
        horizontalScrollbarSize: 0,
      },
      lineDecorationsWidth: 0,
      folding: false,
      glyphMargin: false,
      lineNumbers: 'off',
    });
  }, [diffEditor]);

  const selectedSchemaIsSupergraph = selectedSchema === SUPERGRAPH_SCHEMA_ID;
  const groupedViolationsByLineNumber = useMemo(() => {
    const violationsByLocation = violationsBySubgraph[selectedSchema]?.flatMap(
      (violation) => {
        const locations =
          violation.sourceLocations?.filter((location) =>
            selectedSchemaIsSupergraph
              ? location.subgraphName === null
              : location.subgraphName === selectedSchema,
          ) ?? [];
        return locations.map((location) => {
          return {
            ...violation,
            locations: [location],
          };
        });
      },
    );

    return groupBy(violationsByLocation, (violation) =>
      isMinLengthArray(1, violation.locations)
        ? getLastFromMinLengthArray(violation.locations).end?.line
        : 0,
    );
  }, [selectedSchema, violationsBySubgraph, selectedSchemaIsSupergraph]);

  const linesToShow = useMemo(
    () =>
      Object.keys(groupedViolationsByLineNumber).map((line) =>
        parseInt(line, 10),
      ),
    [groupedViolationsByLineNumber],
  );
  const { modifiedHiddenRanges, originalHiddenRanges, handleExpandRange } =
    useHideUnchangedLines({
      diffEditor,
      linesToShow,
    });

  const shouldShowCollapsedSections =
    !!originalTypeDefs &&
    !!modifiedTypeDefs &&
    !!originalHiddenRanges &&
    !!modifiedHiddenRanges;

  const violations = useMemo(
    () =>
      violationsBySubgraph[selectedSchema]?.filter(
        (diagnostic) =>
          selectedSchemaIsSupergraph ||
          diagnostic.sourceLocations?.some(
            (loc) => loc.subgraphName === selectedSchema,
          ),
      ),
    [selectedSchema, violationsBySubgraph, selectedSchemaIsSupergraph],
  );

  if (documentLoading) {
    return <Loading />;
  }

  return (
    <>
      {shouldShowCollapsedSections && (
        <>
          <HiddenSectionIndicators
            editor={diffEditor.getModifiedEditor()}
            typeDefs={modifiedTypeDefs}
            rangesToHide={modifiedHiddenRanges}
            handleExpandRange={handleExpandRange}
          />
          <HiddenSectionIndicators
            editor={diffEditor.getOriginalEditor()}
            typeDefs={originalTypeDefs}
            rangesToHide={originalHiddenRanges}
            handleExpandRange={handleExpandRange}
          />
        </>
      )}
      {Object.entries(groupedViolationsByLineNumber).map(
        ([lineNumber, violationsAtLineNumber]) => (
          <EditorViolation
            key={lineNumber}
            editor={diffEditor.getModifiedEditor()}
            lineNumber={parseInt(lineNumber, 10)}
            violations={violationsAtLineNumber}
            rules={rules}
            subgraphName={selectedSchemaIsSupergraph ? null : selectedSchema}
            ignoredLinterViolations={ignoredLinterViolations}
          />
        ),
      )}
      <div className="rounded border border-primary">
        <div className="flex items-center justify-between border-b border-b-primary px-4 py-2">
          {typeAndFieldCount && (
            <TypeAndFieldDiffCount
              modifiedSchemaTypeCount={
                typeAndFieldCount.modifiedSchemaTypeCount
              }
              modifiedSchemaFieldCount={
                typeAndFieldCount.modifiedSchemaFieldCount
              }
              fieldsAdded={typeAndFieldCount.fieldsAdded}
              fieldsRemoved={typeAndFieldCount.fieldsRemoved}
              typesAdded={typeAndFieldCount.typesAdded}
              typesRemoved={typeAndFieldCount.typesRemoved}
              className="text-sm"
            />
          )}
          <div className="flex items-center">
            {/* TODO(Linter): This doesnt work well in storybook, but it will likely
                work when on the site. If not, just remove it as it is able to be
                scoped out of the first iteration. */}
            {isMinLengthArray(1, violations) && (
              <LintDiffNavigatorButtons
                editor={diffEditor}
                selectedSchema={selectedSchema}
                violations={violations}
              />
            )}
            <WrapLinesButton
              wrap={shouldWrapLines}
              onClick={() => setShouldWrapLines(!shouldWrapLines)}
            />
          </div>
        </div>
        <div className="[&_.editor.modified]:!left-0 [&_.editor.original]:hidden [&_.monaco-editor_.margin]:border-r [&_.monaco-editor_.margin]:border-primary [&_.monaco-editor_.view-lines]:pl-3">
          <StudioDiffEditorContainer
            diffEditor={diffEditor}
            className="h-120 w-[calc(100%+5px)]"
          />
        </div>
      </div>
    </>
  );
};
